import React, { useEffect, useRef, useState } from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, List, ListItem, Box } from '@mui/material';
import Dialog from './ActionDialog';
import { useTranslation } from 'react-i18next';
import { decideJoinRequest } from '../services/GameService';

export default function HostJoinRequests({ roomId, requests = [], onChanged }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState([]);
    const [batchBusy, setBatchBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [handled, setHandled] = useState([]);
    const [open, setOpen] = useState(false);
    const previousIds = useRef([]);
    const latest = useRef(requests);
    latest.current = requests;
    const session = useRef(null);
    useEffect(() => {
        const current = { active: true, busy: new Set(), handled: new Set(), batch: false };
        session.current = current;
        setBusy([]); setBatchBusy(false); setErrors({}); setHandled([]); setOpen(false);
        previousIds.current = [];
        return () => { current.active = false; };
    }, [roomId]);
    useEffect(() => {
        const ids = requests.map(request => request.requestId);
        if (ids.some(id => !previousIds.current.includes(id))) setOpen(true);
        previousIds.current = ids;
    }, [requests, roomId]);
    const pending = requests.filter(request => !handled.includes(request.requestId));
    const decide = async (requestId, approve, current = session.current) => {
        if (!current?.active || current.busy.has(requestId) || current.handled.has(requestId) ||
            !latest.current.some(request => request.requestId === requestId)) return;
        current.busy.add(requestId);
        setBusy([...current.busy]);
        setErrors(previous => ({ ...previous, [requestId]: '' }));
        try {
            await decideJoinRequest(roomId, requestId, approve);
            if (!current.active) return;
            current.handled.add(requestId);
            setHandled([...current.handled]);
            await onChanged();
        } catch (_) {
            if (current.active && !current.handled.has(requestId) && latest.current.some(request => request.requestId === requestId))
                setErrors(previous => ({ ...previous, [requestId]: t('roomEntry.decisionError') }));
        } finally {
            current.busy.delete(requestId);
            if (current.active) setBusy([...current.busy]);
        }
    };
    const approveAll = async () => {
        const current = session.current;
        if (!current?.active || current.batch || current.busy.size) return;
        current.batch = true; setBatchBusy(true);
        // New arrivals remain pending; only approve the list the host saw.
        // The server remains authoritative for capacity, expiry and admission.
        const ids = pending.map(request => request.requestId);
        try {
            for (const id of ids) {
                if (!current.active) break;
                await decide(id, true, current);
            }
        } finally {
            current.batch = false;
            if (current.active) setBatchBusy(false);
        }
    };
    return <>
        {!open && pending.length > 0 && <Button variant="contained" onClick={() => setOpen(true)}
            sx={{ position: 'fixed', top: '3.4rem', right: '0.75rem', zIndex: 1100 }}>
            {t('roomEntry.viewRequests', { count: pending.length })}
        </Button>}
        <Dialog open={open && pending.length > 0} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="host-join-title">
        <DialogTitle id="host-join-title">{t('roomEntry.requestTitle')}</DialogTitle>
        <DialogContent dividers>
            <p>{t('roomEntry.timeoutInfo')}</p>
            <List aria-label={t('roomEntry.requestTitle')}>
                {pending.map(request => <ListItem key={request.requestId} divider sx={{ display: 'block', px: 0 }}>
                    <p>{t('roomEntry.requestMessage', { playerName: request.playerName || '' })}</p>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button disabled={batchBusy || busy.includes(request.requestId)} onClick={() => decide(request.requestId, false)}>{t('roomEntry.reject')}</Button>
                        <Button disabled={batchBusy || busy.includes(request.requestId)} variant="contained" onClick={() => decide(request.requestId, true)}>{t('roomEntry.approve')}</Button>
                    </Box>
                    {errors[request.requestId] && <p role="alert">{errors[request.requestId]}</p>}
                </ListItem>)}
            </List>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
            <Button disabled={pending.length < 2 || batchBusy || busy.length > 0} variant="contained" onClick={approveAll}>{t('roomEntry.approveAll')}</Button>
        </DialogActions>
    </Dialog></>;
}
